import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Phone,
  Mail,
  ClipboardList,
  ArrowRight,
  Camera,
  Zap,
  Plug,
  BatteryCharging,
  Trees,
  Store,
  ShieldCheck,
  Wrench,
  CheckCircle2,
  Lightbulb,
  Network,
  CalendarCheck,
  HardHat,
  PhoneCall,
  Clock,
  CalendarDays,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ContactCta } from "@/components/site/ContactCta";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import adviesOverlegImg from "@/assets/advies-overleg.png.asset.json";
import adviesOverleg2Img from "@/assets/advies-overleg-2.png.asset.json";


export const Route = createFileRoute("/advies")({
  head: () => ({
    meta: [
      { title: "Advies op maat | Elvion Group Nederland helpt u graag verder" },
      {
        name: "description",
        content:
          "Vrijblijvend advies over camerasystemen, groepenkasten, laadpalen, 1-fase naar 3-fase, tuinelektra en winkelrenovaties. Elvion Group Nederland helpt u verder.",
      },
      { property: "og:title", content: "Advies op maat | Elvion Group Nederland helpt u graag verder" },
      {
        property: "og:description",
        content:
          "Onze specialisten denken met u mee — van een extra groep tot een compleet winkelproject. Elvion Group Nederland helpt u graag verder met persoonlijk advies.",
      },
      { property: "og:url", content: "https://elvion-group.nl/advies" },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/advies" }],
  }),
  component: AdviesPage,
});

const topics = [
  {
    icon: Camera,
    title: "Camerasystemen",
    body: "Hoeveel camera's heb je nodig, welke resolutie past en hoe lang wil je terug kunnen kijken? We adviseren over Hikvision en Dahua opstellingen, AcuSense/WizSense, opslag en privacy (AVG).",
  },
  {
    icon: Zap,
    title: "Groepenkasten",
    body: "Vervangen of uitbreiden? We helpen je kiezen uit het AEG-assortiment, het juiste aantal groepen en de overstap naar aardlekautomaten conform NEN 1010.",
  },
  {
    icon: Plug,
    title: "Uitbreiding groepenkast",
    body: "Advies bij het toevoegen van een groep voor laadpaal, inductie- of elektrisch koken, airco of warmtepomp | inclusief de juiste beveiligingen en kabeldiktes.",
  },
  {
    icon: BatteryCharging,
    title: "1-fase naar 3-fase",
    body: "We bekijken of verzwaring nodig is, wat de netbeheerder vraagt en welke groepen het beste over de drie fasen verdeeld worden voor een stabiele belasting.",
  },
  {
    icon: Trees,
    title: "Elektra in de tuin",
    body: "Welke kabel (YMvKas), welke diepte, en hoe pak je buitenstopcontacten, sfeerverlichting en een laadpunt op de oprit waterdicht en vorstvrij aan?",
  },
  {
    icon: Store,
    title: "Winkelrenovaties",
    body: "Voor verbouwingen denken we mee over verlichting, kassagroepen, camera's, toegangscontrole en automatisering | gepland buiten openingstijden waar mogelijk.",
  },
  {
    icon: Network,
    title: "Netwerken & wifi",
    body: "UTP/PoE-bekabeling, switches, access points en VLAN's. We adviseren over een stabiel netwerk dat past bij je camera's, kassa's en thuiswerken.",
  },
  {
    icon: Lightbulb,
    title: "LED-verlichting",
    body: "Energiezuinige verlichting voor woning, kantoor of winkel | met advies over kleurtemperatuur, lichtsterkte en de juiste fittingen of armaturen.",
  },
];

const channels = [
  {
    icon: Phone,
    title: "Telefonisch",
    desc: "Direct contact met een adviseur",
    actionLabel: "085 080 66 95",
    actionHref: "tel:+31850806695",
  },
  {
    icon: Mail,
    title: "E-mail",
    desc: "Stuur uw vraag per mail",
    actionLabel: "info@elvion-group.nl",
    actionHref: "mailto:info@elvion-group.nl",
  },
];

const process = [
  {
    icon: PhoneCall,
    title: "1. Stel uw vraag",
    body: "Bel of mail ons en omschrijf kort uw situatie. We luisteren en stellen gerichte vragen.",
  },
  {
    icon: ClipboardList,
    title: "2. Persoonlijk advies",
    body: "Een specialist denkt met u mee en stelt een passende oplossing voor uw project samen.",
  },
  {
    icon: CalendarCheck,
    title: "3. Offerte op maat",
    body: "Een transparante offerte met materiaal, arbeid en planning | geen verrassingen achteraf.",
  },
  {
    icon: HardHat,
    title: "4. Uitvoering",
    body: "Akkoord? Dan plannen onze monteurs de installatie netjes en NEN 1010 conform.",
  },
];

const promises = [
  "Vakkundig advies van ervaren installateurs",
  "Gratis en vrijblijvend",
  "Snelle reactie binnen 1 werkdag",
  "Heldere communicatie en planning",
  "A-merken uit eigen voorraad",
  "Landelijk werkgebied",
];

const FALLBACK_OPENING_HOURS = [
  { day_order: 1, label: "Maandag - Vrijdag", hours: "08:30 - 17:00", is_closed: false },
  { day_order: 2, label: "Zaterdag", hours: "Op afspraak", is_closed: false },
  { day_order: 3, label: "Zondag & feestdagen", hours: "Gesloten", is_closed: true },
];

const WEEKDAY_NAMES = [
  "zondag",
  "maandag",
  "dinsdag",
  "woensdag",
  "donderdag",
  "vrijdag",
  "zaterdag",
];
const WEEKDAY_INDEX: Record<string, number> = {
  zondag: 7,
  maandag: 1,
  dinsdag: 2,
  woensdag: 3,
  donderdag: 4,
  vrijdag: 5,
  zaterdag: 6,
};

function labelMatchesToday(label: string, todayName: string, todayIdx: number): boolean {
  const lower = label.toLowerCase();
  const rangeMatch = lower.match(
    /(zondag|maandag|dinsdag|woensdag|donderdag|vrijdag|zaterdag)\s*(?:-|–|t\/m|tot)\s*(zondag|maandag|dinsdag|woensdag|donderdag|vrijdag|zaterdag)/,
  );
  if (rangeMatch) {
    const start = WEEKDAY_INDEX[rangeMatch[1]];
    const end = WEEKDAY_INDEX[rangeMatch[2]];
    if (start <= end) return todayIdx >= start && todayIdx <= end;
    return todayIdx >= start || todayIdx <= end;
  }
  return lower.includes(todayName);
}

function parseHoursRange(hours: string): { open: number; close: number } | null {
  const m = hours.match(/(\d{1,2}):(\d{2})\s*(?:-|–|tot)\s*(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return {
    open: parseInt(m[1], 10) * 60 + parseInt(m[2], 10),
    close: parseInt(m[3], 10) * 60 + parseInt(m[4], 10),
  };
}

type OpenStatus = { isOpen: boolean; label: string; detail: string };

function computeOpenStatus(
  openingHours: { day_order: number; label: string; hours: string; is_closed: boolean }[],
  exceptions: { exception_date: string; label: string; hours: string; is_closed: boolean }[],
  now: Date,
): OpenStatus {
  const todayISO = now.toISOString().slice(0, 10);
  const todayName = WEEKDAY_NAMES[now.getDay()];
  const todayIdx = now.getDay() === 0 ? 7 : now.getDay();
  const minutes = now.getHours() * 60 + now.getMinutes();

  const ex = exceptions.find((e) => e.exception_date === todayISO);
  if (ex) {
    if (ex.is_closed) return { isOpen: false, label: "Vandaag gesloten", detail: ex.label };
    const range = parseHoursRange(ex.hours);
    if (range && minutes >= range.open && minutes < range.close)
      return { isOpen: true, label: "Nu geopend", detail: `${ex.label} · ${ex.hours}` };
    return { isOpen: false, label: "Nu gesloten", detail: `${ex.label} · ${ex.hours}` };
  }

  const match = openingHours.find((o) => labelMatchesToday(o.label, todayName, todayIdx));
  if (!match) return { isOpen: false, label: "Vandaag gesloten", detail: "Geen openingstijden" };
  if (match.is_closed) return { isOpen: false, label: "Vandaag gesloten", detail: match.label };

  const range = parseHoursRange(match.hours);
  if (range) {
    const parts = match.hours.split(/\s*(?:-|–|tot)\s*/);
    if (minutes < range.open)
      return { isOpen: false, label: "Nu gesloten", detail: `Vandaag open vanaf ${parts[0]}` };
    if (minutes >= range.close)
      return { isOpen: false, label: "Nu gesloten", detail: "Vandaag al gesloten" };
    return { isOpen: true, label: "Nu geopend", detail: `Vandaag tot ${parts[1]}` };
  }
  return { isOpen: false, label: "Op afspraak", detail: `${match.label} · ${match.hours}` };
}

function AdviesPage() {
  const { data: openingHours } = useQuery({
    queryKey: ["public-opening-hours"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("opening_hours")
        .select("day_order, label, hours, is_closed")
        .order("day_order", { ascending: true });
      if (error) throw error;
      return data && data.length > 0 ? data : FALLBACK_OPENING_HOURS;
    },
    initialData: FALLBACK_OPENING_HOURS,
  });

  const today = new Date().toISOString().slice(0, 10);
  const horizon = new Date();
  horizon.setDate(horizon.getDate() + 60);
  const horizonISO = horizon.toISOString().slice(0, 10);

  const { data: exceptions } = useQuery({
    queryKey: ["public-opening-hours-exceptions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("opening_hours_exceptions")
        .select("id, exception_date, label, hours, is_closed, note")
        .gte("exception_date", today)
        .lte("exception_date", horizonISO)
        .order("exception_date", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    initialData: [],
  });

  return (
    <>
      <Header variant="shop" />
      <main className="bg-white">

        <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
          <ContactCta
            imageLeft
            imageUrl={adviesOverleg2Img.url}
            imageAlt="Elvion Group Nederland adviseurs in gesprek met een klant over een installatieplan"
            title="Kosteloos adviesgesprek inplannen?"
            description="Onze elektrotechnisch adviseurs kijken samen met u naar uw situatie en werken een passend plan uit — geheel vrijblijvend en zonder verplichtingen."
          />
        </div>

        {/* Hero */}
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                Elektrotechnisch advies op maat — ELVION Groep
              </h1>
              <p className="mt-4 max-w-xl text-base text-text-secondary sm:text-lg">
                Twijfelt u welke oplossing het beste bij uw situatie past? Onze
                adviseurs denken met u mee | van een extra groep in de meterkast
                tot een compleet camerasysteem of winkelrenovatie. Snel,
                vrijblijvend en zonder verkooppraat.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/klantenservice"
                  className="inline-flex items-center gap-2 rounded-md bg-orange px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange/90"
                >
                  Neem contact op
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/offerte-aanvragen"
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
                >
                  Offerte opvragen
                </Link>
              </div>
              <ul className="mt-6 grid gap-2 text-sm text-text-secondary sm:grid-cols-2">
                {promises.slice(0, 4).map((p) => (
                  <li key={p} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-orange" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-orange/20 via-orange/5 to-transparent blur-2xl" />
              <div className="relative rounded-2xl border border-border bg-card p-8 shadow-lg">
                <h2 className="font-display text-lg font-bold text-foreground">
                  Bereik onze adviseurs
                </h2>
                <p className="mt-1 text-sm text-text-secondary">
                  Kies de manier die u het beste uitkomt | we reageren snel.
                </p>
                <div className="mt-5 space-y-3">
                  {channels.map((c) => (
                    <div
                      key={c.title}
                      className="flex items-start gap-4 rounded-xl border border-border bg-background p-4"
                    >
                      <div className="rounded-full bg-orange/10 p-2.5 text-orange">
                        <c.icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-foreground">{c.title}</p>
                        <p className="text-xs text-text-secondary">{c.desc}</p>
                        {c.actionHref ? (
                          <a
                            href={c.actionHref}
                            className="mt-1 inline-block text-sm font-semibold text-orange hover:underline"
                          >
                            {c.actionLabel}
                          </a>
                        ) : (
                          <span className="mt-1 inline-block text-sm font-medium text-foreground/70">
                            {c.actionLabel}
                          </span>
                        )}
                      </div>
                  </div>
                ))}
                </div>

                <div className="mt-5 rounded-xl border border-border bg-background p-4">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-orange">
                      <Clock className="h-4 w-4" />
                      <span className="text-xs font-semibold uppercase tracking-wider">
                        Openingstijden
                      </span>
                    </div>
                    {(() => {
                      const status = computeOpenStatus(openingHours, exceptions ?? [], new Date());
                      return (
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                            status.isOpen
                              ? "bg-green-100 text-green-700"
                              : "bg-muted text-muted-foreground"
                          }`}
                          title={status.detail}
                        >
                          <span
                            className={`inline-block h-1.5 w-1.5 rounded-full ${
                              status.isOpen ? "bg-green-600" : "bg-muted-foreground"
                            } ${status.isOpen ? "animate-pulse" : ""}`}
                          />
                          {status.label}
                        </span>
                      );
                    })()}
                  </div>
                  {(() => {
                    const status = computeOpenStatus(openingHours, exceptions ?? [], new Date());
                    return (
                      <p className="mb-2 text-xs text-text-secondary">{status.detail}</p>
                    );
                  })()}
                  <ul className="space-y-1.5 text-sm">
                    {openingHours.map((o) => (
                      <li key={o.day_order} className="flex items-center justify-between">
                        <span className="text-text-secondary">{o.label}</span>
                        <span
                          className={`font-semibold ${
                            o.is_closed ? "text-muted-foreground" : "text-foreground"
                          }`}
                        >
                          {o.hours}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {exceptions && exceptions.length > 0 && (
                    <div className="mt-3 rounded-xl border border-orange/30 bg-orange/5 p-4">
                      <div className="mb-2 flex items-center gap-2 text-orange">
                        <CalendarDays className="h-4 w-4" />
                        <span className="text-xs font-semibold uppercase tracking-wider">
                          Afwijkende openingstijden
                        </span>
                      </div>
                      <ul className="space-y-1.5 text-sm">
                        {exceptions.map((e) => (
                          <li key={e.id} className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-medium text-foreground">{e.label}</p>
                              <p className="text-xs text-text-secondary">
                                {new Date(e.exception_date + "T00:00:00").toLocaleDateString("nl-NL", {
                                  weekday: "short",
                                  day: "numeric",
                                  month: "long",
                                })}
                                {e.note ? ` · ${e.note}` : ""}
                              </p>
                            </div>
                            <span
                              className={`shrink-0 text-sm font-semibold ${
                                e.is_closed ? "text-muted-foreground" : "text-foreground"
                              }`}
                            >
                              {e.hours}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Topics grid */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                Waarover adviseren wij?
              </h2>
              <p className="mt-3 text-base text-text-secondary">
                Onze adviseurs zijn breed inzetbaar | van een kleine uitbreiding
                tot een groot project op het gebied van elektrotechniek,
                beveiliging en automatisering.
              </p>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {topics.map((t) => (
                <div
                  key={t.title}
                  className="group rounded-xl border border-border bg-background p-6 transition-all hover:border-orange hover:shadow-md"
                >
                  <div className="inline-flex rounded-lg bg-orange/10 p-3 text-orange transition-colors group-hover:bg-orange group-hover:text-white">
                    <t.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-display text-base font-bold text-foreground">
                    {t.title}
                  </h3>
                  <p className="mt-2 text-sm text-text-secondary">{t.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Zo werkt ons advies
            </h2>
            <p className="mt-3 text-base text-text-secondary">
              Een transparant traject | van uw eerste vraag tot een werkende
              installatie.
            </p>
          </div>

          <ol className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {process.map((step) => (
              <li
                key={step.title}
                className="rounded-xl border border-border bg-card p-6"
              >
                <div className="inline-flex rounded-lg bg-orange/10 p-3 text-orange">
                  <step.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-display text-base font-bold text-foreground">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm text-text-secondary">{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Detailed sections */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl space-y-12 px-4 py-14 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                  Het juiste camerasysteem kiezen
                </h2>
                <p className="mt-3 text-text-secondary">
                  We bekijken samen welke gebieden u wilt zien, hoe lang u terug
                  wilt kunnen kijken en of nachtzicht of AI-detectie nodig is.
                  Vervolgens adviseren we de juiste mix van Hikvision of Dahua
                  camera's, een passende NVR en koppeling met Hik-Connect of
                  DMSS.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-text-secondary">
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Advies over resolutie, lens en dekking</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Privacy-instellingen en AVG-checklist</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Mogelijkheid tot installatie door eigen monteurs</li>
                </ul>
              </div>
              <div>
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                  Groepenkast plaatsen of uitbreiden
                </h2>
                <p className="mt-3 text-text-secondary">
                  Een veilige meterkast is het hart van iedere installatie. We
                  adviseren over voorgemonteerde kasten van AEG,
                  aardlekautomaten conform de nieuwste NEN 1010 en de juiste
                  groepenverdeling | ook bij uitbreiding voor een laadpaal,
                  inductiekoken, airco of warmtepomp.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-text-secondary">
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Inspectie en risicoanalyse op locatie</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> NEN 1010 conform opgeleverd</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Inclusief opleverrapport en stickers</li>
                </ul>
              </div>
              <div>
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                  Van 1-fase naar 3-fase
                </h2>
                <p className="mt-3 text-text-secondary">
                  Meer vermogen nodig voor een laadpaal, warmtepomp of
                  werkplaats? We adviseren over de aanvraag van een zwaardere
                  aansluiting bij de netbeheerder, de ombouw van de meterkast
                  en de optimale herverdeling van de groepen over drie fasen.
                </p>
              </div>
              <div>
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                  Elektra aanleggen in de tuin
                </h2>
                <p className="mt-3 text-text-secondary">
                  Buitenstopcontacten, sfeerverlichting, een vijverpomp of een
                  laadpunt op de oprit? We helpen u kiezen tussen kabels,
                  diepte en de juiste beveiliging | zodat alles waterdicht,
                  vorstvrij en netjes weggewerkt wordt aangelegd.
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-background p-8 lg:p-10">
              <div className="flex items-start gap-4">
                <div className="hidden rounded-lg bg-orange/10 p-3 text-orange sm:inline-flex">
                  <Store className="h-7 w-7" />
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                    Advies bij winkelrenovaties
                  </h2>
                  <p className="mt-3 text-text-secondary">
                    Een nieuwe winkel openen of een bestaande zaak verbouwen?
                    We denken mee met aannemers, interieurbouwers en
                    winkelketens over het complete elektrotechnische en
                    beveiligingsplaatje | vaak buiten openingstijden zodat de
                    winkel gewoon door kan draaien.
                  </p>
                  <div className="mt-6 grid gap-4 sm:grid-cols-3">
                    <div className="rounded-lg border border-border p-4">
                      <ShieldCheck className="h-5 w-5 text-orange" />
                      <p className="mt-2 text-sm font-semibold text-foreground">Beveiliging</p>
                      <p className="mt-1 text-xs text-text-secondary">
                        Camera's, alarm en toegangscontrole.
                      </p>
                    </div>
                    <div className="rounded-lg border border-border p-4">
                      <Zap className="h-5 w-5 text-orange" />
                      <p className="mt-2 text-sm font-semibold text-foreground">Elektrotechniek</p>
                      <p className="mt-1 text-xs text-text-secondary">
                        Krachtstroom, kassagroepen en verlichting.
                      </p>
                    </div>
                    <div className="rounded-lg border border-border p-4">
                      <Wrench className="h-5 w-5 text-orange" />
                      <p className="mt-2 text-sm font-semibold text-foreground">Automatisering</p>
                      <p className="mt-1 text-xs text-text-secondary">
                        Schakelklokken, sensoren en domotica.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why us */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Zo helpt Elvion Group Nederland u met advies
          </h2>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {promises.map((p) => (
              <div
                key={p}
                className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3"
              >
                <CheckCircle2 className="h-5 w-5 shrink-0 text-orange" />
                <span className="text-sm font-medium text-foreground">{p}</span>
              </div>
            ))}
          </div>
        </section>

        <ContactCta
          imageUrl={adviesOverlegImg.url}
          imageAlt="Elvion Group Nederland adviseur in gesprek met klanten over een installatieplan"
          title="Advies op maat voor uw project?"
          description="Van eerste schets tot definitieve installatie: onze adviseurs begeleiden u door het hele traject en zorgen dat elke keuze past bij uw wensen en budget."
        />

        {/* CTA */}
        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-6 rounded-2xl bg-primary p-8 text-primary-foreground sm:flex-row sm:p-10">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/10">
                <ClipboardList className="size-6 text-white" />
              </div>
              <div>
                <h3 className="font-display text-xl font-bold">Liever direct een offerte?</h3>
                <p className="mt-1 text-white/80">
                  Vraag gratis en vrijblijvend een offerte aan voor uw project.
                </p>
              </div>
            </div>
            <Link
              to="/offerte-aanvragen"
              className="inline-flex items-center gap-2 rounded-lg bg-orange px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-orange/90"
            >
              Offerte opvragen
              <ArrowRight className="size-4" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
